import React, { FC } from 'react'
import SALink from 'components/SALink'
import { getLogout } from 'apis/authentication'
import StudioIcon from 'svg/studio.svg'
import styles from './Account.module.scss'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { getProfileUrl, getUserID, getUserTypeId, isAuthenticated } from 'selectors/user/session'

export const ARTIST_TYPE = 2

type AccountLinksDropDownProps = {
  dropdownIsOpen: boolean
  dropdownIsHovered: boolean
}

type Props = {
  profileUrl: string
  userId: number
}

const ArtistLinks: FC<Props> = ({ profileUrl, userId }) => {
  return (
    <>
      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/studio'
        rel='nofollow'
      >
        Studio
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href={profileUrl}
        rel='nofollow'
      >
        View Artist Profile
      </SALink>

      <span className={styles.accountDropDownContentLine} />

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href={`/u/${userId}`}
        rel='nofollow'
      >
        View Profile
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href={`/account/favorite/${userId}`}
        rel='nofollow'
      >
        Favorites
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href={`/account/collection/${userId}`}
        rel='nofollow'
      >
        Collections
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/accounts/orders'
        rel='nofollow'
      >
        Orders
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/accounts/offers'
        rel='nofollow'
      >
        Offers
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/accounts/settings'
        rel='nofollow'
      >
        Account
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/cart'
        rel='nofollow'
      >
        Cart
      </SALink>
    </>
  )
}

const CollectorLinks: FC<Props> = ({ profileUrl, userId }) => {
  return (
    <>
      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href={`${profileUrl}`}
        rel='nofollow'
      >
        View Profile
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href={`/account/favorite/${userId}`}
        rel='nofollow'
      >
        Favorites
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href={`/account/collection/${userId}`}
        rel='nofollow'
      >
        Collections
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/accounts/orders'
        rel='nofollow'
      >
        Orders
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/accounts/offers'
        rel='nofollow'
      >
        Offers
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/accounts/settings'
        rel='nofollow'
      >
        Account
      </SALink>

      <SALink
        data-gtm-event='top-nav'
        className={classNames('defaultTypographyMedium', styles.accountDropDownContentAccountLink)}
        href='/cart'
        rel='nofollow'
      >
        Cart
      </SALink>

      <span className={styles.accountDropDownContentLine} />

      <SALink
        data-gtm-event='top-nav'
        className={classNames(
          'defaultTypographyMedium',
          styles.accountDropDownContentAccountBecome,
          styles.accountDropDownContentAccountLink
        )}
        href='/whysell'
        rel='nofollow'
      >
        <StudioIcon />
        <span>Become an Artist</span>
      </SALink>
    </>
  )
}

const AccountLinksDropDown: FC<AccountLinksDropDownProps> = ({
  dropdownIsOpen,
  dropdownIsHovered,
}) => {
  const userTypeId = useSelector(getUserTypeId)
  const authenticated = useSelector(isAuthenticated)
  const profileUrl = useSelector(getProfileUrl)
  const userId = useSelector(getUserID)

  if (!authenticated) {
    return null
  }

  return (
    <div
      className={classNames(styles.accountDropDownContent, styles.accountDropDownContentAccount, {
        [styles.accountDropDownContentOpen]: dropdownIsOpen || dropdownIsHovered,
      })}
      data-type='account-dropdown-content'
    >
      {userTypeId === ARTIST_TYPE ? (
        <ArtistLinks profileUrl={profileUrl} userId={userId} />
      ) : (
        <CollectorLinks profileUrl={profileUrl} userId={userId} />
      )}

      <span className={styles.accountDropDownContentLine} />

      <SALink
        data-gtm-event='top-nav'
        className={classNames(
          'defaultTypographyMedium',
          styles.accountDropDownContentAccountLogout,
          styles.accountDropDownContentAccountLink
        )}
        href='/easel_api/auth/logout'
        onClick={async (event: React.SyntheticEvent<HTMLElement>) => {
          event.preventDefault()
          // API will DELETE session and we refresh the page
          await getLogout()
          location.reload()
        }}
        rel='nofollow'
      >
        Log Out
      </SALink>
    </div>
  )
}

export default AccountLinksDropDown
